import React, { useState, useEffect } from 'react';
import { RefreshCw, ChevronDown, ChevronUp, AlertTriangle, TrendingUp, TrendingDown, ExternalLink, Shield, Users, Building, Globe } from 'lucide-react';

// Add smooth scroll behavior
if (typeof document !== 'undefined') {
  document.documentElement.style.scrollBehavior = 'smooth';
}

const PolicyDevelopmentsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [policyData, setPolicyData] = useState(null);
  const [expandedEO, setExpandedEO] = useState(null);
  const [expandedBill, setExpandedBill] = useState(null);
  const [expandedState, setExpandedState] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterThreat, setFilterThreat] = useState('all');
  const [modalState, setModalState] = useState(null);

  useEffect(() => {
    fetchPolicyData();
  }, []);

  const fetchPolicyData = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://pivotintel.org/app/api/policy-developments.php');
      const result = await response.json();
      
      if (result.success) {
        setPolicyData(result.data);
      } else {
        setError(result.error || 'Failed to load policy data');
      }
    } catch (err) {
      setError('Error fetching policy data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return 'N/A';
    const value = parseFloat(num);
    if (isNaN(value)) return 'N/A';
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toLocaleString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const parseJSON = (str) => {
    if (!str) return null;
    if (typeof str === 'object') return str;
    try {
      return JSON.parse(str);
    } catch (e) {
      return null;
    }
  };

  // Modal Component
  const Modal = ({ isOpen, onClose, children, title }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-[9999] overflow-y-auto" onClick={onClose}>
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          {/* Background overlay */}
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" aria-hidden="true"></div>

          {/* Center modal */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

          <div 
            className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full z-[10000]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="mt-4 max-h-[70vh] overflow-y-auto">
                {children}
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={onClose}
                className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-red-100 text-red-800',
      'enacted': 'bg-green-100 text-green-800',
      'passed_senate': 'bg-blue-100 text-blue-800',
      'passed_house': 'bg-blue-100 text-blue-800',
      'committee': 'bg-yellow-100 text-yellow-800',
      'introduced': 'bg-gray-100 text-gray-800',
      'failed': 'bg-gray-100 text-gray-600'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPolicyCategoryColor = (category) => {
    const colors = {
      'worker_protection': 'bg-green-100 text-green-800',
      'state_preemption': 'bg-red-100 text-red-800',
      'child_safety': 'bg-blue-100 text-blue-800',
      'export_control': 'bg-purple-100 text-purple-800',
      'china_decoupling': 'bg-orange-100 text-orange-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getStanceIcon = (stance) => {
    switch (stance) {
      case 'pro_regulation': return <Shield className="w-4 h-4 text-blue-600" />;
      case 'worker_focused': return <Users className="w-4 h-4 text-green-600" />;
      case 'pro_innovation': return <TrendingUp className="w-4 h-4 text-purple-600" />;
      case 'industry_friendly': return <Building className="w-4 h-4 text-orange-600" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading policy intelligence...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 text-center mb-4">{error}</p>
          <button
            onClick={fetchPolicyData}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const filteredLegislation = policyData.federalLegislation.filter(bill => {
    if (filterCategory === 'all') return true;
    return bill.policy_category === filterCategory;
  });

  const filteredStates = policyData.stateActivity.filter(state => {
    if (filterThreat === 'all') return true;
    if (filterThreat === 'threatened') return state.federal_grant_funding_at_risk;
    if (filterThreat === 'active') return state.bills_enacted > 0;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Policy Developments Intelligence
              </h1>
              <p className="text-gray-600">
                Federal & State AI Policy Tracking • {policyData.dataMonth}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Last updated: {policyData.lastUpdated}
              </p>
            </div>
            <button
              onClick={fetchPolicyData}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Sticky Navigation Bar */}
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm mb-6 -mx-6 px-6 py-3">
          <div className="max-w-7xl mx-auto flex gap-6 overflow-x-auto">
            <a 
              href="#federal" 
              className="whitespace-nowrap px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded transition"
            >
              Federal Policy
            </a>
            <a 
              href="#export-controls" 
              className="whitespace-nowrap px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded transition"
            >
              Cross-Border / Export Controls
            </a>
            <a 
              href="#state-policy" 
              className="whitespace-nowrap px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded transition"
            >
              State Policy
            </a>
            <a 
              href="#international" 
              className="whitespace-nowrap px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded transition opacity-50 cursor-not-allowed"
            >
              International (Coming Soon)
            </a>
          </div>
        </div>

        {/* Top Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Active Federal EOs</p>
                <p className="text-3xl font-bold text-gray-900">{policyData.metrics.active_eos}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Federal Bills Pending</p>
                <p className="text-3xl font-bold text-gray-900">{policyData.metrics.pending_bills}</p>
              </div>
              <TrendingUp className="w-10 h-10 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">State AI Laws Enacted</p>
                <p className="text-3xl font-bold text-gray-900">{policyData.metrics.state_laws_enacted}</p>
              </div>
              <Shield className="w-10 h-10 text-green-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">States Threatened</p>
                <p className="text-3xl font-bold text-gray-900">{policyData.metrics.states_threatened}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Smuggling Alert */}
        {policyData.smugglingSummary && policyData.smugglingSummary.total_value > 0 && (
          <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6 rounded">
            <div className="flex items-start">
              <Globe className="w-5 h-5 text-orange-500 mt-0.5 mr-3" />
              <div>
                <h3 className="font-semibold text-orange-900">Export Control Alert</h3>
                <p className="text-sm text-orange-800 mt-1">
                  <strong>{formatNumber(policyData.smugglingSummary.total_value)}</strong> in NVIDIA chips smuggled to China in last 6 months 
                  ({policyData.smugglingSummary.incident_count} incidents, {policyData.smugglingSummary.total_arrests} arrests)
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Federal Executive Orders */}
        <div id="federal" className="mb-6 scroll-mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            Federal Executive Orders
          </h2>

          <div className="space-y-4">
            {policyData.executiveOrders.map((eo) => (
              <div key={eo.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div
                  className="p-6 cursor-pointer hover:bg-gray-50 transition"
                  onClick={() => setExpandedEO(expandedEO === eo.id ? null : eo.id)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-mono text-gray-600">{eo.eo_number}</span>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(eo.status)}`}>
                          {eo.status.replace('_', ' ').toUpperCase()}
                        </span>
                        {eo.preempts_state_law && (
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-semibold">
                            PREEMPTS STATES
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{eo.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">Signed: {formatDate(eo.signed_date)}</p>
                      <p className="text-sm text-gray-700">{eo.summary}</p>
                    </div>
                    {expandedEO === eo.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400 ml-4" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 ml-4" />
                    )}
                  </div>
                </div>

                {expandedEO === eo.id && (
                  <div className="px-6 pb-6 border-t border-gray-100 pt-4">
                    {eo.key_provisions && (
                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-900 mb-3 text-lg">Key Provisions:</h4>
                        <div className="text-base text-gray-700 whitespace-pre-line leading-relaxed">{eo.key_provisions}</div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      {eo.worker_impact_summary && (
                        <div className="bg-red-50 p-4 rounded">
                          <h5 className="font-semibold text-base text-red-900 mb-2 flex items-center gap-1">
                            <Users className="w-5 h-5" /> Worker Impact
                          </h5>
                          <p className="text-base text-red-800 leading-relaxed">{eo.worker_impact_summary}</p>
                        </div>
                      )}

                      {eo.community_impact_summary && (
                        <div className="bg-orange-50 p-4 rounded">
                          <h5 className="font-semibold text-base text-orange-900 mb-2 flex items-center gap-1">
                            <Building className="w-5 h-5" /> Community Impact
                          </h5>
                          <p className="text-base text-orange-800 leading-relaxed">{eo.community_impact_summary}</p>
                        </div>
                      )}

                      {eo.state_impact_summary && (
                        <div className="bg-purple-50 p-4 rounded">
                          <h5 className="font-semibold text-base text-purple-900 mb-2 flex items-center gap-1">
                            <Shield className="w-5 h-5" /> State Impact
                          </h5>
                          <p className="text-base text-purple-800 leading-relaxed">{eo.state_impact_summary}</p>
                        </div>
                      )}
                    </div>

                    {(eo.full_text_url || eo.source_url) && (
                      <div className="mt-4 flex gap-4">
                        {eo.full_text_url && (
                          <a
                            href={eo.full_text_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <ExternalLink className="w-4 h-4" /> Full Text
                          </a>
                        )}
                        {eo.source_url && (
                          <a
                            href={eo.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <ExternalLink className="w-4 h-4" /> Source
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Federal Legislation */}
        <div className="mb-6 scroll-mt-24">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-blue-600" />
              Federal Legislation
            </h2>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All Categories</option>
              <option value="worker_protection">Worker Protection</option>
              <option value="state_preemption">State Preemption</option>
              <option value="child_safety">Child Safety</option>
              <option value="export_control">Export Control</option>
              <option value="china_decoupling">China Decoupling</option>
            </select>
          </div>

          <div className="space-y-4">
            {filteredLegislation.map((bill) => (
              <div key={bill.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div
                  className="p-6 cursor-pointer hover:bg-gray-50 transition"
                  onClick={() => setExpandedBill(expandedBill === bill.id ? null : bill.id)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-mono text-gray-600">{bill.bill_number}</span>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(bill.status)}`}>
                          {bill.status.replace('_', ' ').toUpperCase()}
                        </span>
                        {bill.policy_category && (
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getPolicyCategoryColor(bill.policy_category)}`}>
                            {bill.policy_category.replace('_', ' ').toUpperCase()}
                          </span>
                        )}
                        {bill.bipartisan && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                            BIPARTISAN
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{bill.short_title}</h3>
                      <p className="text-sm text-gray-600 mb-2">
                        Sponsor: {bill.primary_sponsor} ({bill.primary_sponsor_party}-{bill.primary_sponsor_state})
                        {bill.introduced_date && ` • Introduced: ${formatDate(bill.introduced_date)}`}
                        {bill.bipartisan && (
                          <>
                            {(() => {
                              const cosponsors = parseJSON(bill.cosponsors);
                              if (cosponsors && Array.isArray(cosponsors) && cosponsors.length > 0) {
                                return ` • Co-sponsor: ${cosponsors[0].name} (${cosponsors[0].party}-${cosponsors[0].state})`;
                              }
                              return '';
                            })()}
                          </>
                        )}
                      </p>
                      <p className="text-sm text-gray-700">{bill.summary}</p>
                    </div>
                    {expandedBill === bill.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400 ml-4" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 ml-4" />
                    )}
                  </div>
                </div>

                {expandedBill === bill.id && (
                  <div className="px-6 pb-6 border-t border-gray-100 pt-4">
                    {bill.key_provisions && (
                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-900 mb-3 text-lg">Key Provisions:</h4>
                        <div className="text-base text-gray-700 whitespace-pre-line leading-relaxed">{bill.key_provisions}</div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      {bill.worker_impact && (
                        <div className="bg-green-50 p-4 rounded">
                          <h5 className="font-semibold text-base text-green-900 mb-2">Worker Impact:</h5>
                          <p className="text-base text-green-800 leading-relaxed">{bill.worker_impact}</p>
                        </div>
                      )}

                      {bill.likelihood_of_passage && (
                        <div className="bg-blue-50 p-4 rounded">
                          <h5 className="font-semibold text-base text-blue-900 mb-2">Likelihood of Passage:</h5>
                          <p className="text-base text-blue-800 capitalize leading-relaxed">{bill.likelihood_of_passage}</p>
                          {bill.industry_position && (
                            <p className="text-base text-blue-700 mt-1">Industry: {bill.industry_position}</p>
                          )}
                        </div>
                      )}
                    </div>

                    {bill.source_url && (
                      <div className="mt-4">
                        <a
                          href={bill.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <ExternalLink className="w-4 h-4" /> Source
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Federal-Private Partnerships */}
        {policyData.federalPartnerships && policyData.federalPartnerships.length > 0 && (
          <div className="mb-6 scroll-mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Building className="w-6 h-6 text-indigo-600" />
              Federal-Private AI Partnerships
            </h2>

            <div className="space-y-4">
              {policyData.federalPartnerships.map((partnership) => {
                const companies = parseJSON(partnership.private_companies);
                const agencies = parseJSON(partnership.federal_agencies);
                
                return (
                  <div key={partnership.id} className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-sm text-gray-600">{formatDate(partnership.announced_date)}</span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            partnership.significance === 'major' ? 'bg-red-100 text-red-800' :
                            partnership.significance === 'moderate' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {partnership.significance.toUpperCase()}
                          </span>
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs font-semibold">
                            {partnership.partnership_type.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{partnership.partnership_name}</h3>
                        
                        <div className="flex gap-4 mb-3 text-sm">
                          {agencies && agencies.length > 0 && (
                            <div>
                              <span className="font-semibold text-gray-700">Federal: </span>
                              <span className="text-gray-600">{agencies.join(', ')}</span>
                            </div>
                          )}
                          {companies && companies.length > 0 && (
                            <div>
                              <span className="font-semibold text-gray-700">Companies: </span>
                              <span className="text-gray-600">
                                {companies.slice(0, 3).join(', ')}
                                {companies.length > 3 && ` +${companies.length - 3} more`}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <p className="text-base text-gray-800 mb-3 leading-relaxed">{partnership.description}</p>
                        
                        {partnership.scope && (
                          <p className="text-sm text-gray-600 mb-3">
                            <strong>Scope:</strong> {partnership.scope}
                          </p>
                        )}

                        {partnership.bottom_line && (
                          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 rounded mt-3">
                            <p className="text-sm text-yellow-900 font-medium">
                              <strong>Bottom Line:</strong> {partnership.bottom_line}
                            </p>
                          </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                          {partnership.worker_impact_summary && (
                            <div className="bg-red-50 p-3 rounded">
                              <h5 className="font-semibold text-sm text-red-900 mb-1">Worker Impact:</h5>
                              <p className="text-sm text-red-800 leading-relaxed">{partnership.worker_impact_summary}</p>
                            </div>
                          )}

                          {partnership.community_impact_summary && (
                            <div className="bg-orange-50 p-3 rounded">
                              <h5 className="font-semibold text-sm text-orange-900 mb-1">Community Impact:</h5>
                              <p className="text-sm text-orange-800 leading-relaxed">{partnership.community_impact_summary}</p>
                            </div>
                          )}
                        </div>

                        {partnership.announcement_url && (
                          <div className="mt-3">
                            <a
                              href={partnership.announcement_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            >
                              <ExternalLink className="w-4 h-4" /> Official Announcement
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Export Controls Section */}
        {policyData.exportControlEvents && policyData.exportControlEvents.length > 0 && (
          <div id="export-controls" className="mb-6 scroll-mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Globe className="w-6 h-6 text-purple-600" />
              NVIDIA/China Export Controls
            </h2>

            <div className="space-y-3">
              {policyData.exportControlEvents.slice(0, 10).map((event) => (
                <div key={event.id} className="bg-white rounded-lg shadow-md p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs text-gray-600">{formatDate(event.event_date)}</span>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          event.is_smuggling ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {event.event_type.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-1">{event.title}</h3>
                      <p className="text-sm text-gray-700 mb-2">{event.description}</p>
                      {event.is_smuggling && event.value_smuggled && (
                        <p className="text-sm text-red-600 font-semibold">
                          Value: {formatNumber(event.value_smuggled)}
                          {event.arrests_made > 0 && ` • ${event.arrests_made} arrests`}
                        </p>
                      )}
                    </div>
                    {event.source_url && (
                      <a
                        href={event.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 ml-4"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* State AI Policy Activity Section */}
        {policyData.stateActivity && policyData.stateActivity.length > 0 && (
          <div id="state-policy" className="mb-6 scroll-mt-24">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Shield className="w-6 h-6 text-green-600" />
                State AI Policy Activity
              </h2>
              <select
                value={filterThreat}
                onChange={(e) => setFilterThreat(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">All States</option>
                <option value="threatened">Threatened by Federal</option>
                <option value="active">Active Legislation</option>
              </select>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        State
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stance
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bills Introduced
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Enacted
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Federal Threat
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredStates.map((state) => (
                      <tr key={state.state} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">{state.state}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            state.regulatory_stance === 'proactive' ? 'bg-green-100 text-green-800' :
                            state.regulatory_stance === 'moderate' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {state.regulatory_stance}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {state.bills_introduced}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {state.bills_enacted}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {state.federal_grant_funding_at_risk ? (
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-red-600" />
                              <span className="text-xs text-red-600 font-semibold">
                                {state.estimated_grants_at_risk ? formatNumber(state.estimated_grants_at_risk) : 'YES'}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">No</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => {
                              // Find major laws for this state
                              const stateLaws = policyData.majorStateLaws.filter(law => law.state === state.state);
                              setModalState({ ...state, laws: stateLaws });
                            }}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary Note */}
            <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
              <p className="text-sm text-yellow-800">
                <strong>2025 State Activity:</strong> All 50 states + DC + territories introduced AI legislation. 
                260 bills across 40 states, 22+ enacted. December 11 EO threatens grant funding for states enforcing 
                AI laws conflicting with federal priorities.
              </p>
            </div>
          </div>
        )}

        {/* Data Sources */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
          <h3 className="font-semibold text-blue-900 mb-2">Data Sources</h3>
          <p className="text-sm text-blue-800">
            Federal EOs: White House official records • Federal Legislation: Congress.gov • 
            State Laws: State legislative databases • Export Controls: DOJ, Financial Times, news reports
          </p>
          <p className="text-xs text-blue-700 mt-2">
            Updated weekly for Under the Radar newsletter. All sources archived via Wayback Machine.
          </p>
        </div>

        {/* State Details Modal */}
        <Modal 
          isOpen={modalState !== null} 
          onClose={() => setModalState(null)}
          title={modalState ? `${modalState.state} AI Policy Activity` : ''}
        >
          {modalState && (
            <div className="space-y-6 text-base">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs text-gray-600 mb-1">Regulatory Stance</p>
                  <p className="text-lg font-bold text-gray-900 capitalize">{modalState.regulatory_stance}</p>
                </div>
                <div className="bg-blue-50 p-3 rounded">
                  <p className="text-xs text-blue-600 mb-1">Bills Introduced</p>
                  <p className="text-lg font-bold text-blue-900">{modalState.bills_introduced}</p>
                </div>
                <div className="bg-green-50 p-3 rounded">
                  <p className="text-xs text-green-600 mb-1">Bills Enacted</p>
                  <p className="text-lg font-bold text-green-900">{modalState.bills_enacted}</p>
                </div>
                <div className="bg-yellow-50 p-3 rounded">
                  <p className="text-xs text-yellow-600 mb-1">Bills Pending</p>
                  <p className="text-lg font-bold text-yellow-900">{modalState.bills_pending}</p>
                </div>
              </div>

              {modalState.federal_grant_funding_at_risk && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <h4 className="text-lg font-bold text-red-900">Federal Threat Active</h4>
                  </div>
                  <p className="text-red-800 leading-relaxed">
                    This state faces potential loss of federal grant funding under December 11 EO if it enforces 
                    AI regulations conflicting with federal priorities.
                    {modalState.estimated_grants_at_risk && (
                      <> Estimated at risk: <strong>{formatNumber(modalState.estimated_grants_at_risk)}</strong></>
                    )}
                  </p>
                </div>
              )}

              {modalState.major_laws_enacted && (
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Major Laws Enacted</h4>
                  <div className="bg-green-50 p-4 rounded-lg text-green-900 leading-relaxed">
                    {modalState.major_laws_enacted}
                  </div>
                </div>
              )}

              {modalState.laws && modalState.laws.length > 0 && (
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Detailed Legislation</h4>
                  <div className="space-y-4">
                    {modalState.laws.map((law) => (
                      <div key={law.id} className="border-l-4 border-blue-500 pl-4 py-2">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-mono text-sm text-gray-600">{law.bill_number}</span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(law.status)}`}>
                            {law.status.replace('_', ' ').toUpperCase()}
                          </span>
                          {law.threatened_by_federal && (
                            <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-semibold">
                              THREATENED
                            </span>
                          )}
                        </div>
                        <h5 className="font-semibold text-gray-900 mb-1">{law.title}</h5>
                        <p className="text-sm text-gray-700 mb-2">{law.summary}</p>
                        {law.worker_protections && (
                          <p className="text-sm text-green-800 bg-green-50 p-2 rounded">
                            <strong>Worker Protections:</strong> {law.worker_protections}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-sm text-yellow-900 leading-relaxed">
                  <strong>Note:</strong> State-level policy is moving rapidly. Local townships and counties are 
                  increasingly resisting federal fast-tracking and data center proposals. This data represents 
                  major bills only - full activity tracking requires CSV-style rapid updates.
                </p>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default PolicyDevelopmentsDashboard;